import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { PRIORITY_LEVELS } from "../lib/tasks";
import { AUTONOMY_CHOICES, activationMissing, followupDefaults, validatePlanning } from "../lib/pilots";
import { Loader2 } from "lucide-react";

const SELF = "__self__";

// Every dialog closes with Esc or a click outside (Radix default) and disables its confirm button while a request is in
// flight, so a double click can never send twice.
function Shell({ open, onClose, title, description, children, footer, testid }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={testid} className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        <DialogFooter className="gap-2 sm:gap-2">{footer}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Confirm({ busy, disabled, onClick, children, testid }) {
  return (
    <Button  disabled={busy || disabled} onClick={onClick} data-testid={testid}>
      {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}
      {children}
    </Button>
  );
}

function DateFields({ start, end, onStart, onEnd, error }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="pilot-start">Début</Label>
          <Input id="pilot-start" type="date" value={start} onChange={(e) => onStart(e.target.value)} data-testid="pilot-input-start" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pilot-end">Fin</Label>
          <Input id="pilot-end" type="date" value={end} onChange={(e) => onEnd(e.target.value)} data-testid="pilot-input-end" />
        </div>
      </div>
      {error && <p role="alert" className="text-xs text-red-600" data-testid="pilot-dates-error">{error}</p>}
    </div>
  );
}

export function ResponsibleSelect({ value, onChange, directory, selfId, id = "pilot-responsible" }) {
  return (
    <Select value={value || SELF} onValueChange={(v) => onChange(v === SELF ? selfId : v)}>
      <SelectTrigger id={id} data-testid="pilot-select-responsible"><SelectValue /></SelectTrigger>
      <SelectContent>
        {directory.map((u) => <SelectItem key={u.id} value={u.id === selfId ? SELF : u.id}>{u.name}{u.id === selfId ? " (moi)" : ""}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function CreatePilotDialog({ open, onClose, onSubmit, busy, directory, user, pharmacyName }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [responsible, setResponsible] = useState(user?.id);
  useEffect(() => { if (open) { setStart(""); setEnd(""); setResponsible(user?.id); } }, [open, user?.id]);
  const error = validatePlanning({ start, end });
  return (
    <Shell open={open} onClose={onClose} testid="dialog-pilot-create" title="Créer le pilote"
      description={`${pharmacyName} deviendra pharmacie pilote. Le pilote sera créé « Planifié » : rien ne change dans le pipeline commercial.`}
      footer={<><Button variant="ghost" onClick={onClose}>Retour</Button>
        <Confirm busy={busy} disabled={!!error} testid="pilot-confirm-create" onClick={() => onSubmit({ start_date: start || null, end_date: end || null, responsible_user_id: responsible })}>Créer le pilote</Confirm></>}>
      <div className="space-y-4">
        <DateFields start={start} end={end} onStart={setStart} onEnd={setEnd} error={error} />
        <p className="text-xs text-slate-500">Les dates peuvent être renseignées plus tard, mais elles sont indispensables pour activer le pilote.</p>
        {directory.length > 0 ? (
          <div className="space-y-1.5">
            <Label htmlFor="pilot-responsible">Responsable du suivi</Label>
            <ResponsibleSelect value={responsible} onChange={setResponsible} directory={directory} selfId={user?.id} />
          </div>
        ) : <p className="text-xs text-slate-500">Vous serez désigné responsable du pilote.</p>}
      </div>
    </Shell>
  );
}

export function ActivatePilotDialog({ pilot, open, onClose, onSubmit, busy }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  useEffect(() => { if (open && pilot) { setStart(pilot.start_date || ""); setEnd(pilot.end_date || ""); } }, [open, pilot]);
  const missing = activationMissing({ start, end });
  const error = validatePlanning({ start, end });
  return (
    <Shell open={open} onClose={onClose} testid="dialog-pilot-activate" title="Activer le pilote"
      description="L'activation est manuelle et indépendante de l'onboarding. Elle ne modifie pas le statut commercial de la pharmacie."
      footer={<><Button variant="ghost" onClick={onClose}>Retour</Button>
        <Confirm busy={busy} disabled={missing.length > 0 || !!error} testid="pilot-confirm-activate" onClick={() => onSubmit({ start, end })}>Activer le pilote</Confirm></>}>
      <DateFields start={start} end={end} onStart={setStart} onEnd={setEnd} error={error} />
      {missing.length > 0 && <p className="text-xs text-amber-700" data-testid="pilot-activate-missing">Renseignez {missing.join(" et ")} pour activer le pilote.</p>}
    </Shell>
  );
}

export function CompletePilotDialog({ open, onClose, onSubmit, busy }) {
  const [note, setNote] = useState("");
  useEffect(() => { if (open) setNote(""); }, [open]);
  return (
    <Shell open={open} onClose={onClose} testid="dialog-pilot-complete" title="Terminer le pilote"
      description="Action définitive : un pilote terminé ne peut pas être rouvert. Son état (santé, autonomie, avancement) est conservé tel quel à la clôture. Le statut commercial de la pharmacie ne change pas et les tâches ouvertes restent ouvertes."
      footer={<><Button variant="ghost" onClick={onClose}>Retour</Button>
        <Confirm busy={busy} testid="pilot-confirm-complete" onClick={() => onSubmit(note.trim() || null)}>Terminer le pilote</Confirm></>}>
      <div className="space-y-1.5">
        <Label htmlFor="pilot-complete-note">Note de clôture (facultative)</Label>
        <Textarea id="pilot-complete-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={3} data-testid="pilot-complete-note" />
      </div>
    </Shell>
  );
}

export function CancelPilotDialog({ open, onClose, onSubmit, busy }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  return (
    <Shell open={open} onClose={onClose} testid="dialog-pilot-cancel" title="Annuler le pilote"
      description="Action définitive : un pilote annulé ne peut pas être rouvert. Le statut commercial de la pharmacie ne change pas et les tâches ouvertes restent ouvertes."
      footer={<><Button variant="ghost" onClick={onClose}>Retour</Button>
        <Confirm busy={busy} disabled={!reason.trim()} testid="pilot-confirm-cancel" onClick={() => onSubmit(reason.trim())}>Annuler le pilote</Confirm></>}>
      <div className="space-y-1.5">
        <Label htmlFor="pilot-cancel-reason">Motif d'annulation (obligatoire)</Label>
        <Textarea id="pilot-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} rows={3} data-testid="pilot-cancel-reason" />
      </div>
    </Shell>
  );
}

export function AutonomyDialog({ pilot, open, onClose, onSubmit, busy }) {
  const [level, setLevel] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { setLevel(""); setNote(""); } }, [open, pilot]);
  return (
    <Shell open={open} onClose={onClose} testid="dialog-pilot-autonomy" title="Évaluer l'autonomie"
      description="Évaluation manuelle : elle est datée et attribuée à votre compte. L'historique des évaluations est conservé dans le journal."
      footer={<><Button variant="ghost" onClick={onClose}>Retour</Button>
        <Confirm busy={busy} disabled={!level} testid="pilot-confirm-autonomy" onClick={() => onSubmit({ level, note: note.trim() || null })}>Enregistrer l'évaluation</Confirm></>}>
      <div className="space-y-4">
        <RadioGroup value={level} onValueChange={setLevel} className="space-y-2">
          {AUTONOMY_CHOICES.map((c) => (
            <label key={c.id} htmlFor={`autonomy-${c.id}`} className="flex items-start gap-2 rounded-md border border-slate-200 p-2.5 cursor-pointer">
              <RadioGroupItem id={`autonomy-${c.id}`} value={c.id} data-testid={`autonomy-choice-${c.id}`} className="mt-0.5" />
              <span><span className="text-sm font-medium text-slate-900">{c.label}</span><span className="block text-xs text-slate-500">{c.hint}</span></span>
            </label>
          ))}
        </RadioGroup>
        <div className="space-y-1.5">
          <Label htmlFor="autonomy-note">Commentaire (facultatif)</Label>
          <Textarea id="autonomy-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={2} data-testid="autonomy-note" />
        </div>
      </div>
    </Shell>
  );
}

// An EXPLICIT proposal to plan a pilot follow-up: opened by a click, never by itself. Domain and type are fixed
// (pilots / suivi_pilote); the due date is mandatory because a follow-up without one is not a "next follow-up".
export function FollowupDialog({ open, onClose, onSubmit, busy, pharmacyName, options, defaultAssignee, user }) {
  const [form, setForm] = useState(followupDefaults(pharmacyName));
  const [assignees, setAssignees] = useState([]);
  useEffect(() => {
    if (!open) return;
    setForm(followupDefaults(pharmacyName));
    setAssignees([defaultAssignee || user?.id]);
  }, [open, pharmacyName, defaultAssignee, user?.id]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (id) => setAssignees((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  const choosing = options.length > 0;
  const ready = form.title.trim() && form.due_date && assignees.length > 0;
  return (
    <Shell open={open} onClose={onClose} testid="dialog-pilot-followup" title="Planifier un suivi du pilote"
      description="Crée une tâche de suivi (domaine « Pharmacies pilotes ») rattachée à cette pharmacie. Rien n'est créé tant que vous ne confirmez pas."
      footer={<><Button variant="ghost" onClick={onClose}>Retour</Button>
        <Confirm busy={busy} disabled={!ready} testid="pilot-confirm-followup"
          onClick={() => onSubmit({ title: form.title.trim(), due_date: form.due_date, priority: form.priority, assigned_to: choosing ? assignees : undefined })}>Planifier le suivi</Confirm></>}>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="followup-title">Intitulé</Label>
          <Input id="followup-title" value={form.title} onChange={(e) => set("title")(e.target.value)} data-testid="followup-title" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="followup-due">Échéance (obligatoire)</Label>
            <Input id="followup-due" type="date" value={form.due_date} onChange={(e) => set("due_date")(e.target.value)} data-testid="followup-due" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="followup-priority">Priorité</Label>
            <Select value={form.priority} onValueChange={set("priority")}>
              <SelectTrigger id="followup-priority" data-testid="followup-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITY_LEVELS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {choosing ? (
          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium">Assigné à</legend>
            <div className="max-h-36 overflow-y-auto rounded-md border border-slate-200 p-2 space-y-1.5">
              {options.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={assignees.includes(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`followup-assignee-${u.id}`} />
                  {u.name}{u.id === user?.id ? " (moi)" : ""}
                </label>
              ))}
            </div>
          </fieldset>
        ) : <p className="text-xs text-slate-500" data-testid="followup-self-note">Le suivi vous sera assigné.</p>}
      </div>
    </Shell>
  );
}

// Sharing / revoking is explicit: the dialog lists, in clear, exactly the numbers concerned.
export function SharingDialog({ open, mode, rows, onClose, onSubmit, busy }) {
  const revoke = mode === "revoke";
  return (
    <Shell open={open} onClose={onClose} testid="dialog-pilot-sharing" title={revoke ? "Révoquer tout le partage" : "Partager ces coordonnées"}
      description={revoke
        ? "Le responsable du pilote ne verra plus aucun de ces numéros. Vous pourrez les partager de nouveau plus tard."
        : "Ces numéros seront visibles par le responsable du pilote (et par lui seul, dans l'espace pilote). Vérifiez qu'il s'agit bien de numéros professionnels que la pharmacie accepte de partager."}
      footer={<><Button variant="ghost" onClick={onClose}>Retour</Button>
        <Confirm busy={busy} testid="pilot-confirm-sharing" onClick={onSubmit}>{revoke ? "Révoquer le partage" : "Partager"}</Confirm></>}>
      {!revoke && (
        <ul className="space-y-1.5 text-sm" data-testid="sharing-confirm-list">
          {rows.map((r) => <li key={r.key} className="flex justify-between gap-3 rounded-md bg-slate-50 px-3 py-2"><span className="text-slate-600">{r.label}</span><span className="font-medium text-slate-900">{r.value}</span></li>)}
          {rows.length === 0 && <li className="text-slate-500">Aucun numéro sélectionné : tout partage en cours sera supprimé.</li>}
        </ul>
      )}
    </Shell>
  );
}
